<template>
  <div class="flex items-start justify-center min-h-screen" v-if="loading">
    <Spinner></Spinner>
  </div>
  <div v-else-if="session.length === 0">
    <p class="text-center">No session found.</p>
  </div>
  <div v-else>
    <div class="border-b p-6 pb-4 rounded-md mb-4 bg-white">
      <div class="flex justify-between mb-1">
        <div class="">
          <div class="text-2xl font-medium ">{{ session.yogaType.title }}</div>
          <div class="mt-1 text-lg">{{ session.friendlyTime }}</div>
        </div>
        <div>
          <button v-if="students.length == 0" @click="showAddSession = true" class="g--button g--button--small">Edit
            session
          </button>
          <a target="_blank" :href="session.url"
             class="ml-4 g--button g--button--small">View booking page</a></div>
      </div>
      <div class="flex gap-2">
        <div v-for="tag in session.tags" class="g--tag  items-center">
          <span class="mr-2">{{ tag.emoji }}</span>
          {{ tag.title }}
        </div>
      </div>
      <div class="mt-2 flex items-center mb-4"><span
          class="text-xl font-lighter  inline-block mr-4">{{ session.priceFriendly }}</span>
      </div>
      <hr/>
      <div class="py-4">
        <h4 class="font-medium">Video links</h4>
        <div class="text-base mt-1 ">Teacher start link</div>
        <div v-if="session.zoomStartLink" class="text-sm text-gray-500 flex items-center gap-2">
          <a target="_blank" class="hover:underline" :href="session.zoomStartLink">{{
              session.zoomStartLink ? session.zoomStartLink.substring(0, 40) : ''
            }}...</a>
          <button @click="handleCopy(session.zoomStartLink, 'startLink')" class="text-gray-400 hover:text-gray-600">
            <img alt="Copy" src="/img/copy.svg" class="w-4 h-4"/>
          </button>
          <span v-if="showStartLinkCopied" class="text-teal font-medium">Copied!</span>
        </div>
        <div v-else class="text-sm text-gray-500">Not ready yet. The video link will be available one hour before the class starts.</div>
        <div class="text-base mt-3 ">Student link</div>
        <div v-if="session.videoLink" class="text-sm text-gray-500">
          <a target="_blank" class="hover:underline" :href="session.videoLink">{{ session.videoLink }}</a>
          <button @click="handleCopy(session.videoLink, 'videoLink')" class="text-gray-400 hover:text-gray-600 ml-2">
            <img alt="Copy" src="/img/copy.svg" class="w-4 h-4"/>
          </button>
          <span v-if="showVideoLinkCopied" class="text-teal font-medium">Copied!</span>
        </div>
        <div v-else class="text-sm text-gray-500">Not ready yet. The video link will be available one hour before the class starts.</div>
      </div>

      <hr/>
      <div class="py-4">
        <h4 class="font-medium">Students</h4>
        <div class="flex flex-col gap-2">
          <div v-if="studentsLoading">
            Loading...
          </div>
          <div v-else-if="students.length == 0 && !studentsLoading">
            No registration.
          </div>
          <div v-else v-for="student in students" :key="student.id"
               class="bg-green/20 border rounded-md p-3 mt-2 flex justify-between items-center">
            <span class="text-base font-medium">{{ student.fullName }}</span>
            <span class="text-sm text-gray-500">{{ student.email }}</span>
          </div>
        </div>
      </div>

      <hr/>
      <div class="flex items-center justify-end pt-4">
        <div v-if="students.length == 0">
          <button @click="showCancelSession = true" class="g--button g--button--small g--button--cancel">Cancel
            session
          </button>
        </div>
        <div class="text-sm italic opacity-60" v-else>
          You cannot edit or cancel this session as there are students registered.
        </div>
      </div>
    </div>

  </div>
  <Teleport to="body">
    <div v-if="showAddSession" class="">
      <AddSession :start-time="session.startDayTime" :duration="session.minutes" :session-id="session.id"
                  :class-item="activeClass" v-if="showAddSession" @click.stop
                  @session-added="showAddSession = false; fetchSession();"></AddSession>
    </div>
    <div v-if="showCancelSession" class="">
      <CancelSession :session-id="session.id" @session-deleted="redirectAfterDeletion()"
                     @session-close="showCancelSession = false" v-if="showCancelSession" @click.stop></CancelSession>
    </div>
  </Teleport>

</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import AddSession from './AddSession.vue';
import CancelSession from './CancelSession.vue';
import Spinner from './Spinner.vue';

const loading = ref(true);
const session = ref([]);
const showEditClass = ref(false);

const showAddSession = ref(false);
const showCancelSession = ref(false);
const studentsLoading = ref(false);

const students = ref([]);

const showStartLinkCopied = ref(false);
const showVideoLinkCopied = ref(false);

const props = defineProps({
  sessionId: {
    type: Number,
    required: true,
  },
});

onMounted(() => {
  fetchSession();
  fetchTeacherSessionInfo();
});

const fetchSession = async () => {
  const response = await fetch('/api/sessions/all?sessionId=' + props.sessionId);
  const data = await response.json();
  session.value = data.sessions[0];
  loading.value = false;
};

const fetchTeacherSessionInfo = async () => {
  const response = await fetch('/api/sessions/teacher-info?sessionId=' + props.sessionId);
  const data = await response.json();
  students.value = data.sessions[0].students;
  loading.value = false;
};


const activeClass = computed(() => {
  return {id: session.value.classId};
});

const redirectAfterDeletion = () => {
  window.location.href = '/teachers/classes';
};

const copyToClipboard = async (text, location) => {
  try {
    await navigator.clipboard.writeText(text);
    if (location == 'startLink') {
      showStartLinkCopied.value = true;
    } else if (location == 'videoLink') {
      showVideoLinkCopied.value = true;
    }
    setTimeout(() => {
      showStartLinkCopied.value = false;
      showVideoLinkCopied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

const handleCopy = (text, ref) => {
  copyToClipboard(text, ref);
};

</script>